import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../commons/Auth';
import { useLoading } from '../../commons/Loading';
import { useAlert } from 'react-alert';
import classnames from 'classnames';
import {
  BtnModalTable,
  BtnOutlineGreen,
  BtnOutlineRed,
  H4Styled,
  H5Styled,
} from '../../styled-components';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap';
import InvoiceTabel from './InvoiceTabel';
import ContractsTabel from './ContractsTabel';
import RecurrentContractsTabel from './RecurrentContractsTabel';
import { createBonus } from '../../lib/fetch/reports';
import moment from 'moment';
import SignalsTabel from './SignalsTabel';

const ModalIafs = ({
  data = [],
  month,
  value,
  year,
  fetchExtraInvoicesByMonth,
}) => {
  const [t] = useTranslation('ROYALTY');
  const [{ newToken, profile = {} } = {}] = useAuth();
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();

  const [modal, setModal] = useState(false);
  const [dataForIafsInvoices, setDataForIafsInvoices] = useState([]);
  const [dataForIafsContracts, setDataForIafsContracts] = useState([]);
  const [dataForIafsRecurrentContracts, setDataForIafsRecurrentContracts] =
    useState([]);
  const [dataForIafsSignals, setDataForIafsSignals] = useState([]);

  const [totalCount, setTotalCount] = useState(0);
  const [totIafs, setTotIafs] = useState(0);
  const [totInvoices, setTotInvoices] = useState(0);
  const [totContracts, setTotContracts] = useState(0);
  const [totRecurrentContracts, setTotRecurrentContracts] = useState(0);
  const [totSignals, setTotSignals] = useState(0);
  const [sentinel, setSentinel] = useState('invoices');

  const toggle = () => {
    setModal(!modal);
  };

  const CURRENT_YEAR = new Date().getFullYear();
  const CURRENT_MONTH = new Date().getMonth() + 1;

  const getColor = ({ month, totalCount, year }) => {
    const notNull = totalCount !== null;
    if (notNull || year > CURRENT_YEAR) return '';

    if (year < CURRENT_YEAR) return notNull ? '' : 'red';
    if (month < CURRENT_MONTH) return notNull ? '' : 'red';
    return '';
  };

  const confirmPreview = async (bonus) => {
    if (!(Number(bonus?.amount) > 0)) {
      alert.error(t('COMMON:ERROR_TOTAL_TO_INVOICE_0'));
      return;
    }

    const { error } = await createBonus({
      newToken,
      bonus: { ...bonus, profileId: data.profileId },
    });

    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }
  };

  const handleConfirm = async () => {
    try {
      showLoading();
      const skippedCategories = [];
      const contractsAdminShareTotal = dataForIafsContracts.reduce(
        (total, { adminShare }) => total + (Number(adminShare) || 0),
        0
      );
      const recurrentContractsAdminShareTotal =
        dataForIafsRecurrentContracts.reduce(
          (total, { adminShare }) => total + (Number(adminShare) || 0),
          0
        );

      if (dataForIafsInvoices.length > 0) {
        await confirmPreview({
          iafType: 'INVOICES',
          amount: totInvoices,
          date: moment.utc().startOf('day').format(),
          accountability: year,
          invoices: dataForIafsInvoices.map(
            ({
              _id,
              accountability,
              adminShare,
              net,
              supplier,
              customer,
              date,
              number,
              typeOfManager,
              invoicePercExtra = 0,
              invoicePercAdmin = 0,
            }) => ({
              _id,
              accountability: accountability || undefined,
              admin: {
                _id: data.profileId,
                businessName: data.businessName,
              },
              earnedAdminShare: adminShare,
              adminShare,
              net,
              supplier,
              customer,
              date,
              number,
              typeOfManager,
              invoicePercExtra,
              invoicePercAdmin,
            })
          ),
        });
      }

      if (dataForIafsContracts.length > 0 && contractsAdminShareTotal > 0) {
        await confirmPreview({
          iafType: 'ENERGY',
          amount: totContracts,
          date: moment.utc().startOf('day').format(),
          accountability: year,
          contracts: dataForIafsContracts.map(
            ({
              _id,
              admin,
              condo,
              adminShare,
              extraShare,
              date,
              number,
              contract_signing_date,
            }) => ({
              _id,
              admin,
              condo,
              earnedAdminShare: adminShare,
              adminShare,
              net: extraShare,
              date,
              number,
              contract_signing_date: new Date(contract_signing_date),
            })
          ),
        });
      } else if (dataForIafsContracts.length > 0) {
        skippedCategories.push(t('IAF_BY_MONTH:ENERGY_CATEGORY'));
      }

      if (
        dataForIafsRecurrentContracts.length > 0 &&
        recurrentContractsAdminShareTotal > 0
      ) {
        await confirmPreview({
          iafType: 'RECURRENT_ENERGY',
          amount: totRecurrentContracts,
          date: moment.utc().startOf('day').format(),
          accountability: year,
          recurrentContracts: dataForIafsRecurrentContracts.map(
            ({ _id, condo, adminShare, net, date, number, dateFee }) => ({
              _id,
              admin: {
                _id: data.profileId,
                businessName: data.businessName,
              },
              condo,
              earnedAdminShare: adminShare,
              adminShare,
              net,
              date,
              number,
              dateFee: new Date(dateFee),
            })
          ),
        });
      } else if (dataForIafsRecurrentContracts.length > 0) {
        skippedCategories.push(t('IAF_BY_MONTH:RECURRENT_ENERGY_CATEGORY'));
      }

      if (dataForIafsSignals.length > 0) {
        const signalsByRole = dataForIafsSignals.reduce((acc, row) => {
          const role = row.matchedRole || 'signalman';
          if (!acc[role]) {
            acc[role] = [];
          }
          acc[role].push(row);
          return acc;
        }, {});

        for (const [role, signals] of Object.entries(signalsByRole)) {
          const roleAmount = signals.reduce(
            (acc, { adminShare = 0 }) => acc + adminShare,
            0
          );

          const data = {
            amount: roleAmount,
            date: moment.utc().startOf('day').format(),
            accountability: year,
            iafType: 'SIGNALS',
            role,
            signals: signals.map(
              ({
                _id,
                accountabilityDate,
                adminInfo,
                adminShare,
                calculateAdminPerc,
                commercial,
                commercial2,
                commercial3,
                createdAt,
                estimate,
                extraPercentage,
                extraShare,
                reportingType,
                shareToInvoice,
                signalTypeInfo,
                signalman,
                signalmanShare,
                signalmanPosition,
                signalmanSharePerc,
                notes,
                matchedRole,
              }) => ({
                _id,
                accountabilityDate,
                admin: {
                  _id: adminInfo?._id,
                  businessName: adminInfo?.businessName,
                  fiscalCode: adminInfo?.fiscalCode,
                },
                adminShare,
                calculateAdminPerc,
                commercial,
                commercial2,
                commercial3,
                createdAt,
                estimate,
                extraPercentage,
                extraShare,
                reportingType,
                shareToInvoice,
                signalTypeInfo,
                signalman: {
                  _id: signalman?._id,
                  businessName: signalman?.businessName,
                  fiscalCode: signalman?.fiscalCode,
                },
                signalmanShare,
                signalmanPosition,
                signalmanSharePerc,
                notes,
                matchedRole,
              })
            ),
          };

          await confirmPreview(data);
        }
      }

      setDataForIafsInvoices([]);
      setDataForIafsContracts([]);
      setDataForIafsRecurrentContracts([]);
      setDataForIafsSignals([]);
      toggle();
      await fetchExtraInvoicesByMonth(true);

      alert.success(t('COMMON:SUCCESS_UPDATE'));
      if (skippedCategories.length > 0) {
        alert.info(
          t('IAF_BY_MONTH:SKIPPED_NON_POSITIVE', {
            categories: skippedCategories.join(', '),
          })
        );
      }
    } catch (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    const invoicesByMonth = data?.invoices || [];
    const contractsByMonth = data?.contracts || [];
    const recurrentContractsByMonth = data?.recurrentContracts || [];
    const signalsByMonth = data?.signals || [];

    const count =
      (invoicesByMonth[month - 1]?.count || 0) +
      (contractsByMonth[month - 1]?.count || 0) +
      (recurrentContractsByMonth[month - 1]?.count || 0) +
      (signalsByMonth[month - 1]?.count || 0);

    setTotalCount(count);
    setDataForIafsInvoices(invoicesByMonth[month - 1]?.invoices || []);
    setDataForIafsContracts(contractsByMonth[month - 1]?.contracts || []);
    setDataForIafsRecurrentContracts(
      recurrentContractsByMonth[month - 1]?.recurrentContracts || []
    );
    setDataForIafsSignals(signalsByMonth[month - 1]?.signals || []);

    const invoices = invoicesByMonth[month - 1]?.invoices || [];
    const contracts = contractsByMonth[month - 1]?.contracts || [];
    const recurrentContracts =
      recurrentContractsByMonth[month - 1]?.recurrentContracts || [];
    const signals = signalsByMonth[month - 1]?.signals || [];

    const sum = [
      ...invoices,
      ...contracts,
      ...recurrentContracts,
      ...signals,
    ].reduce((acc, item) => acc + (item.adminShare || 0), 0);
    const sumInvoices = invoices.reduce(
      (acc, item) => acc + (item.adminShare || 0),
      0
    );
    const sumContracts = contracts.reduce(
      (acc, item) => acc + (item.adminShare || 0),
      0
    );
    const sumRecurrentContracts = recurrentContracts.reduce(
      (acc, item) => acc + (item.adminShare || 0),
      0
    );
    const sumSignals = signals.reduce(
      (acc, item) => acc + (item.adminShare || 0),
      0
    );

    setTotIafs(sum.toFixed(2));
    setTotInvoices(sumInvoices.toFixed(2));
    setTotContracts(sumContracts.toFixed(2));
    setTotRecurrentContracts(sumRecurrentContracts.toFixed(2));
    setTotSignals(sumSignals.toFixed(2));
  }, [data]);

  return (
    <>
      <span
        style={{
          color: getColor({ month, totalCount, year }),
          cursor: 'pointer',
        }}
      >
        <span>Elementi da emettere: {totalCount || 0}</span>
        <br />
        <span>Totale: € {totIafs}</span>
        <br />
        <br />
        <span>Totale fatture: € {totInvoices}</span>
        <br />
        <br />
        <span>Totale energia: € {totContracts}</span>
        <br />
        <br />
        <span>Totale energia ricorrente: € {totRecurrentContracts}</span>
        <br />
        <br />
        <span>Totale segnalazioni: € {totSignals}</span>
        <br />
        <br />
        {totalCount > 0 && (
          <div className="flex-check">
            <BtnModalTable
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                toggle();
              }}
            >
              Emetti IAF
            </BtnModalTable>
          </div>
        )}
      </span>

      <Modal isOpen={modal} className="iafs-modal">
        <ModalHeader toggle={toggle}>
          Anteprima IAF
          <br />
        </ModalHeader>
        <ModalBody>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <h5 style={{ fontFamily: "'Open Sans', sans-serif" }}>
              Totale da emettere: € {totIafs}
            </h5>
            <h5 style={{ fontFamily: "'Open Sans', sans-serif" }}>
              Elementi da emettere: {totalCount}
            </h5>
          </div>

          <Nav tabs>
            <NavItem>
              <NavLink
                className={classnames({ active: sentinel === 'invoices' })}
                onClick={() => setSentinel('invoices')}
                style={{ cursor: 'pointer' }}
              >
                Fatture
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: sentinel === 'contracts' })}
                onClick={() => setSentinel('contracts')}
                style={{ cursor: 'pointer' }}
              >
                Energia
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({
                  active: sentinel === 'recurrentContracts',
                })}
                onClick={() => setSentinel('recurrentContracts')}
                style={{ cursor: 'pointer' }}
              >
                Energia Ricorrente
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({
                  active: sentinel === 'signals',
                })}
                onClick={() => setSentinel('signals')}
                style={{ cursor: 'pointer' }}
              >
                Segnalazioni
              </NavLink>
            </NavItem>
          </Nav>

          {sentinel === 'invoices' && dataForIafsInvoices?.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <InvoiceTabel
                data={dataForIafsInvoices}
                onFilter={() => {}}
                query={{
                  limit: dataForIafsInvoices?.length || 50,
                  offset: 0,
                }}
                total={dataForIafsInvoices?.length}
              />
            </div>
          )}

          {sentinel === 'invoices' && dataForIafsInvoices?.length === 0 && (
            <H4Styled style={{ textAlign: 'center', marginTop: '15px' }}>
              Non ci sono fatture su cui emettere iaf
            </H4Styled>
          )}

          {sentinel === 'contracts' && dataForIafsContracts?.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <ContractsTabel
                data={dataForIafsContracts}
                onFilter={() => {}}
                query={{
                  limit: dataForIafsContracts?.length || 50,
                  offset: 0,
                }}
                total={dataForIafsContracts?.length}
              />
            </div>
          )}

          {sentinel === 'contracts' && dataForIafsContracts?.length === 0 && (
            <H4Styled style={{ textAlign: 'center', marginTop: '15px' }}>
              Non ci sono contratti energia su cui emettere iaf
            </H4Styled>
          )}

          {sentinel === 'recurrentContracts' &&
            dataForIafsRecurrentContracts?.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <RecurrentContractsTabel
                  data={dataForIafsRecurrentContracts}
                  onFilter={() => {}}
                  query={{
                    limit: dataForIafsRecurrentContracts?.length || 50,
                    offset: 0,
                  }}
                  total={dataForIafsRecurrentContracts?.length}
                />
              </div>
            )}

          {sentinel === 'recurrentContracts' &&
            dataForIafsRecurrentContracts?.length === 0 && (
              <H4Styled style={{ textAlign: 'center', marginTop: '15px' }}>
                Non ci sono contratti energia ricorrenti su cui emettere iaf
              </H4Styled>
            )}

          {sentinel === 'signals' && dataForIafsSignals?.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <SignalsTabel
                data={dataForIafsSignals}
                onFilter={() => {}}
                query={{
                  limit: dataForIafsSignals?.length || 50,
                  offset: 0,
                }}
                total={dataForIafsSignals?.length}
              />
            </div>
          )}

          {sentinel === 'signals' && dataForIafsSignals?.length === 0 && (
            <H4Styled style={{ textAlign: 'center', marginTop: '15px' }}>
              Non ci sono segnalazioni su cui emettere iaf
            </H4Styled>
          )}
        </ModalBody>
        <ModalFooter>
          <BtnOutlineRed type="button" className="uppercase" onClick={toggle}>
            {t('COMMON:CANCEL')}
          </BtnOutlineRed>
          {totalCount > 0 && (
            <BtnOutlineGreen
              type="button"
              className="uppercase"
              onClick={handleConfirm}
            >
              Conferma IAF
            </BtnOutlineGreen>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ModalIafs;
